import * as React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { buildDeliveryTimeline, DeliveryInfo, formatTime, pickupDeadline } from './deliveryTimeline';

const labelSx = {
  fontSize: 'var(--text-xs)',
  color: 'var(--color-text-muted)',
  mb: '6px',
  fontWeight: 700,
  letterSpacing: 'var(--tracking-wide)',
  textTransform: 'uppercase',
};

const Line = ({ label, value }: { label: string; value: string }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ gap: 'var(--space-3)' }}>
    <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
  </Stack>
);

/** Stan dostawy Wolt: szukanie kuriera, ETA, os czasu i link sledzenia. */
export const DeliveryTracking: React.FC<{ info: DeliveryInfo }> = ({ info }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const timeline = buildDeliveryTimeline(info);
  const deadline = pickupDeadline(info.pickupEta);
  const dropoff =
    info.dropoffEtaMin && info.dropoffEtaMax && info.dropoffEtaMin !== info.dropoffEtaMax
      ? `${formatTime(info.dropoffEtaMin, locale)}–${formatTime(info.dropoffEtaMax, locale)}`
      : formatTime(info.dropoffEtaMin ?? info.dropoffEtaMax, locale);

  return (
    <Box sx={{ px: 'var(--space-4)', py: 'var(--space-3)' }}>
      <Typography sx={labelSx}>{t('orders.timeline.title')}</Typography>

      {info.woltRetryUntil && (
        <Alert severity="warning" sx={{ mb: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
          {t('orders.timeline.searchingCourier', {
            time: formatTime(info.woltRetryUntil, locale),
          })}
        </Alert>
      )}

      {(info.pickupEta || info.dropoffEtaMin || info.dropoffEtaMax) && (
        <Stack spacing="4px" sx={{ mb: 'var(--space-3)' }}>
          {info.pickupEta && (
            <Line
              label={t('orders.timeline.pickupEta')}
              value={formatTime(info.pickupEta, locale)}
            />
          )}
          {deadline && (
            <Line label={t('orders.timeline.pickupDeadline')} value={formatTime(deadline, locale)} />
          )}
          {(info.dropoffEtaMin || info.dropoffEtaMax) && (
            <Line label={t('orders.timeline.dropoffEta')} value={dropoff} />
          )}
        </Stack>
      )}

      <Stack spacing="6px">
        {timeline.map((entry, index) => (
          <Stack
            key={`${entry.labelKey}-${entry.at}-${index}`}
            direction="row"
            alignItems="baseline"
            sx={{ gap: 'var(--space-3)' }}
          >
            <Typography
              sx={{
                fontSize: 'var(--text-sm)',
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                minWidth: 44,
              }}
            >
              {formatTime(entry.at, locale)}
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)' }}>
              {t(`orders.timeline.${entry.labelKey}`)}
              {entry.vehicle
                ? ` (${t(`orders.timeline.vehicle.${entry.vehicle}`, { defaultValue: entry.vehicle })})`
                : ''}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {info.woltTrackingUrl && (
        <Button
          variant="outlined"
          size="small"
          href={info.woltTrackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mt: 'var(--space-3)', borderRadius: 'var(--radius-md)' }}
        >
          {t('orders.timeline.trackInWolt')}
        </Button>
      )}
    </Box>
  );
};
